import type { BandProfile, Domain, DomainCandidate, Warning } from './types';
import { fmtNum } from './util';

export interface ClassifyInput {
  sampleFormat: number;
  bits: number;
  bands: BandProfile[];
  perBandMeta: { scale: number | null; offset: number | null }[];
  /** True maximum from a full-resolution read, or null if unavailable. */
  ceiling?: number | null;
}

/**
 * Decide how stored samples become physical units.
 *
 * The file's own SCALE/OFFSET always wins. Failing that the stored range is the only
 * evidence, and it is frequently ambiguous — which is why this returns a ranked list
 * rather than an answer. Normalized-difference indices cancel any common scale, so an
 * ambiguous choice affects display brightness and nothing else.
 */
export function classifyDomain(input: ClassifyInput): Domain {
  const { sampleFormat, bits, bands, perBandMeta, ceiling = null } = input;
  const warnings: Warning[] = [];
  const cands: DomainCandidate[] = [];

  // Thermal is Kelvin and alpha is a flag; neither shares units with the optical
  // bands, and one 315 K band is enough to stop four 0-1 bands reading as reflectance.
  const useful = bands.filter((b) => b.role !== 'alpha' && b.role !== 'thermal' && !b.stats.empty);
  const hasThermal = bands.some((b) => b.role === 'thermal');
  const omax = useful.length ? Math.max(...useful.map((b) => b.stats.max)) : 0;
  const gmax = Math.max(omax, ceiling ?? -Infinity);
  const gmin = useful.length ? Math.min(...useful.map((b) => b.stats.min)) : 0;
  const hasMeta = perBandMeta.some((m) => m.scale != null || m.offset != null);
  const namedIndex = useful.some((b) => b.role === 'index');

  if (hasMeta) {
    const sc = perBandMeta.map((m) => m.scale ?? 1);
    const of = perBandMeta.map((m) => m.offset ?? 0);
    cands.push({ id: 'metadata', label: 'Scale + offset declared in the file', perBand: true,
      scales: sc, offsets: of, confidence: 'high',
      note: 'GDAL_METADATA carries SCALE/OFFSET per band. This is the file telling you ' +
        'directly — trust it over any heuristic.',
      formula: `value × ${fmtNum(sc[0])} ${(of[0] ?? 0) < 0 ? '−' : '+'} ${fmtNum(Math.abs(of[0] ?? 0))}` });
    if (of.some((o) => o !== 0)) {
      warnings.push({ level: 'info', text:
        `A non-zero offset (${fmtNum(of.find((o) => o !== 0))}) is declared. Offsets are **not** ` +
        'cancelled by normalized-difference indices — dropping it silently shifts every NDVI value.' });
    }
  }

  if (sampleFormat === 3) {
    if (namedIndex && gmax <= 1.5) {
      cands.push({ id: 'index', label: 'Already an index', scale: 1, offset: 0, confidence: 'high',
        note: 'A band is named as an index in the file. Displayed as-is. (An index over fully ' +
          'vegetated ground never goes negative, so the sign of the minimum is not evidence.)',
        formula: 'value' });
    } else if (gmin < -0.05 && gmax <= 1.5) {
      cands.push({ id: 'index', label: 'Already an index', scale: 1, offset: 0, confidence: 'high',
        note: 'Float values spanning negatives within ±1 — a computed index, not reflectance.',
        formula: 'value' });
    } else if (gmax <= 1.5) {
      cands.push({ id: 'refl-float', label: 'Float reflectance 0–1', scale: 1, offset: 0,
        confidence: 'high', note: 'Already in physical units. No conversion applied.', formula: 'value' });
    } else {
      // Float storage does not imply physical units: plenty of chains write
      // reflectance×10000 into a Float32 raster.
      const raw: DomainCandidate = { id: 'float-raw', label: 'Float, units as stored',
        scale: 1, offset: 0, confidence: 'low',
        note: 'Left exactly as stored. Correct if these are radiance, temperature, elevation ' +
          'or another physical quantity rather than scaled reflectance.', formula: 'value' };
      const DIVS: [number, string][] = [
        [10000, 'Reflectance × 10000'], [32768, 'Reflectance × 32768'],
        [65535, 'Reflectance × 65535'], [4095, '12-bit counts as float'],
      ];
      if (gmax >= 200 && gmax <= 2e5) {
        const fit = DIVS.map(([d, label]) => ({ d, label, err: Math.abs(Math.log(gmax / d)) }))
          .sort((a, b) => a.err - b.err);
        fit.slice(0, 2).forEach((s) => cands.push({ id: 'float-div' + s.d, label: s.label,
          scale: 1 / s.d, offset: 0, confidence: 'low',
          note: `Values reach ${fmtNum(gmax)}, far above 0–1, but dividing by ${s.d} brings them ` +
            'into the reflectance range. Common when software writes scaled reflectance into a float raster.',
          formula: `value ÷ ${s.d}` }));
        cands.push(raw);
        warnings.push({ level: 'warn', text:
          `Float data reaching ${fmtNum(gmax)} is **not 0–1 reflectance**. The best-fitting ` +
          'divisor is applied so values read as reflectance, but this is inferred from the range ' +
          'alone — switch to "units as stored" if these are radiance, temperature or elevation.' });
      } else {
        cands.push(raw);
        warnings.push({ level: 'warn', text:
          `Float data ranging to ${fmtNum(gmax)} — **units are undetermined** and no scaling fits ` +
          'the reflectance range. Ratio indices still work; display stretch needs manual bounds.' });
      }
    }
  } else if (bits === 8) {
    cands.push({ id: 'dn8', label: '8-bit DN', scale: 1 / 255, offset: 0, confidence: 'high',
      note: 'Standard 8-bit imagery, almost always already stretched for display.',
      formula: 'value ÷ 255' });
  } else {
    const R12: DomainCandidate = { id: 'dn12', label: '12-bit DN', scale: 1 / 4095, offset: 0,
      confidence: 'low', note: 'Raw 12-bit sensor counts — typical of drone multispectral cameras.',
      formula: 'value ÷ 4095' };
    const R1e4: DomainCandidate = { id: 'refl-1e4', label: 'Reflectance × 10000',
      scale: 1 / 10000, offset: 0, confidence: 'low',
      note: 'The Sentinel-2 / Harmonized-Landsat convention: reflectance stored as an integer scaled by 10000.',
      formula: 'value ÷ 10000' };
    const R16: DomainCandidate = { id: 'dn16', label: '16-bit DN', scale: 1 / 65535, offset: 0,
      confidence: 'low', note: 'Full-range 16-bit counts.', formula: 'value ÷ 65535' };

    const sat = ceiling != null && ceiling >= 4090 && ceiling <= 4095;
    if (sat) {
      cands.push({ ...R12, confidence: 'medium',
        note: R12.note + ' Values reach exactly 4095 at full resolution — a hard 12-bit ceiling.' },
        { ...R1e4 });
    } else if (gmax > 13000) cands.push({ ...R16, confidence: 'medium' }, { ...R1e4 });
    else if (gmax > 4095) cands.push({ ...R1e4, confidence: 'medium' }, { ...R16 });
    else if (gmax >= 3900) cands.push({ ...R12, confidence: 'medium' }, { ...R1e4 });
    else cands.push({ ...R1e4 }, { ...R12 }, { ...R16 });

    if (!sat && ceiling == null && gmax <= 4095) {
      warnings.push({ level: 'info', text:
        'The observed maximum comes from an averaged overview, which flattens the saturation ' +
        'ceiling that would identify 12-bit data. The true full-resolution maximum is likely higher.' });
    }
  }

  if (hasThermal && !hasMeta) {
    warnings.push({ level: 'info', text:
      'A thermal band is present. It is **excluded from the value-domain scaling** — Kelvin does ' +
      'not share units with reflectance — and is shown as stored counts unless the file declares ' +
      'a per-band scale. Use Band mode to view it.' });
  }
  const ambiguous = !hasMeta && cands.length > 1;
  if (ambiguous) {
    warnings.push({ level: 'info', text:
      'The stored range fits more than one convention and the file declares no scale. ' +
      '**Normalized-difference indices are unaffected** — (a−b)/(a+b) cancels any common scale ' +
      'factor — so this only changes true-colour brightness.' });
  }
  if (sampleFormat !== 3 && !perBandMeta.some((m) => m.offset != null)) {
    warnings.push({ level: 'warn', text:
      'Integer data with **no OFFSET declared**. If this is Landsat Collection 2 surface ' +
      'reflectance, the real offset is −0.2 and omitting it corrupts every index.' });
  }
  return { candidates: cands, selected: 0, ambiguous, warnings };
}

/** Physical scale and offset for one band under the selected domain. */
export function scaleOffsetFor(
  domain: Domain, bandIndex: number, role: string,
): [number, number] {
  const c = domain.candidates[domain.selected];
  if (!c) return [1, 0];
  if (c.perBand) return [c.scales?.[bandIndex] ?? 1, c.offsets?.[bandIndex] ?? 0];
  // A single global scale describes the optical bands only.
  if (role === 'thermal') return [1, 0];
  return [c.scale ?? 1, c.offset ?? 0];
}
